import { ExtendedOffersResponse } from 'app/console-api/offers/offers-extended';
import styled from 'styled-components';
import {
  BodyText,
  Button,
  Dropdown,
  DropdownValue,
  FlexColumn,
  FlexRow,
  SvgIcon,
} from '@make-software/cspr-design';
import { components } from 'react-select';
import LinkIcon from 'assets/icons/ic-link.svg';
import SuccessIcon from 'assets/logos/success-entity.svg';
import {
  ErrorText,
  ErrorTextContainer,
  FormContainer,
  NoEntityInfoWrapper,
  SourceInfoWrapper,
  StyledIconLink,
  StyledTooltip
} from 'app/components/common/common-styles';
import React, { useEffect, useState } from 'react';
import { getOffers, getAppsList, PaginationResponseType, TradeType } from 'app/console-api';
import { requestDeal } from 'app/console-api/deals/deals';
import InfoIcon from 'assets/icons/ic-info.svg';
import { CategoryTag } from 'app/components/common/categories/category-tag';
import TelegramIcon from 'assets/icons/ic-telegram.svg';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { createRequestDealsValidationSchema } from 'app/components/management/apps/deals/common/deals-validation-schemas';
import { HttpError } from 'app/components/utils/http-error';
import { AUTH_TOKEN, customErrorMessage, statusCode } from 'app/constants';
import { HOME_PATH, SIGN_IN_PATH, APPS_CREATE_PATH, APPS_DEALS_VIEW_PATH } from 'app/route/paths';
import SuccessEntityWindow from 'app/components/common/success-entity-modal/success-entity-modal';
import { generatePath, Link, useNavigate } from 'react-router-dom';
import { ErrorResult } from 'app/organizations-api/types';
import { ErrorWindow } from 'app/components/common/error-window/error-window';
import NotFoundIcon from 'assets/logos/no-found.svg';
import ErrorEntityWindow from 'app/components/common/error-entity-modal/error-entity-modal';
import { RewardsType, RewardsValue } from 'app/console-api/apps/types';
import { AppCategory, getAppCategories } from 'app/console-api/apps/categories';
import { getRewardsTypes } from 'app/console-api/apps/rewards';
import { useUrlFilterController } from 'app/components/filters/url-filters';
import { IsActiveAdapter } from 'app/components/filters/is-active-filter';
import { AutomationRequest } from 'app/console-api/automations/types';
import { AutomationsControlsBlock } from 'app/components/management/common/autoamtions/automations-controls-block';
import { DealTradeInfoSnippet } from 'app/components/common/deal-trade-type/deal-trade-info-snppet';
import { getDealInverseType } from 'app/components/common/deal-trade-type/deal-trade-type-meta';
import { DealFormFields } from 'app/components/management/apps/deals/common/deal-form-fields';
import {
  generateDealTitle,
  isFeatureEnabled
} from 'app/components/management/apps/deals/common/trade-type-config';
import { DealTypesAdapter } from 'app/components/filters/deal-types-filter';

const StyledButton = styled(Button)(({ theme }) =>
  theme.withMedia({
    height: '30px',
    marginTop: '48px'
  })
);

const ItemArea = styled(FlexColumn)<{ $withScroll?: boolean }>(({ theme, $withScroll }) =>
  theme.withMedia({
    overflow: $withScroll ? 'scroll' : 'unset',
    borderRadius: '4px',
    background: '#F5F5F7',
    padding: '16px'
  })
);

const StyledCaption = styled.div(({ theme }) =>
  theme.withMedia({
    color: '#1A1919',
    fontSize: '14px',
    fontWeight: '600',
    lineHeight: '20px'
  })
);

const IconWrapper = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    padding: '12px',
    backgroundColor: theme.styleguideColors.backgroundPrimary
  })
);

const StyledTitle = styled(BodyText)(({ theme }) =>
  theme.withMedia({
    display: 'block'
  })
);

const StyledDescription = styled.div(({ theme }) =>
  theme.withMedia({
    fontSize: '13px',
    display: 'block',
    lineHeight: '20px',
    width: ['150px', '200px', '100%'],
    whiteSpace: ['nowrap', 'nowrap', 'unset'],
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: theme.styleguideColors.contentSecondary
  })
);

const StyledLink = styled(Link)(({ theme }) =>
  theme.withMedia({
    fontSize: '14px',
    fontWeight: '600',
    color: theme.styleguideColors.contentBlue,

    ':hover': {
      textDecoration: 'underline'
    }
  })
);

const SubtitleBodyText = styled(BodyText)(({ theme }) =>
  theme.withMedia({
    position: 'relative',
    bottom: '5px',
    lineHeight: ['17px', '20px', '24px']
  })
);

export interface DealsCreateFormProps {
  offer: ExtendedOffersResponse;
}

export interface TsDropdownValue extends DropdownValue {
  type: string;
}

export interface FormValues {
  dealType: TradeType;
  myApp: TsDropdownValue;
  myOffer: DropdownValue;
  rewards: RewardsValue[];
  categories: number[];
  title: string;
  customMetadata: string;
  automations: AutomationRequest[];
}

const { Option, SingleValue } = components;

const DropdownOption = (props: any) => {
  const {
    data: { label, description }
  } = props;
  return (
    <Option {...props}>
      <FlexColumn>
        <FlexRow>
          <SourceInfoWrapper justify={'flex-start'} align={'center'} gap={12}>
            <IconWrapper justify={'center'} align={'center'}>
              <SvgIcon src={TelegramIcon} size={28} />
            </IconWrapper>
            <div>
              <StyledTitle size={1}>{label}</StyledTitle>
              <StyledDescription>{description}</StyledDescription>
            </div>
          </SourceInfoWrapper>
        </FlexRow>
      </FlexColumn>
    </Option>
  );
};

const SelectedDropdownValue = (props: any) => (
  <SingleValue {...props}>{props.data.label}</SingleValue>
);

export const DealsRequestForm = (props: DealsCreateFormProps) => {
  const [appsList, setAppsList] = useState<TsDropdownValue[]>([]);
  const [offerData, setOfferData] = useState<ExtendedOffersResponse[]>([]);
  const [openSuccessModal, setOpenSuccessModal] = useState<boolean>(false);
  const [dealID, setDealID] = useState<string>('');
  const [unexpectedError, setUnexpectedError] = useState<ErrorResult | null>(null);
  const [openConflictErrorModal, setOpenConflictErrorModal] = useState<boolean>(false);
  const [conflictError, setConflictError] = useState<string>('');
  const [appCategories, setAppCategories] = useState<PaginationResponseType<AppCategory> | null>(
    null
  );
  const [rewardsTypes, setRewardsTypes] = useState<RewardsType[]>([]);
  const [formSubmitInProgress, setFormSubmitInProgress] = useState<boolean>(false);
  const filterController = useUrlFilterController();

  const [formData, setFormData] = useState<FormValues>({
    dealType: getDealInverseType(props.offer.offer.tradeType),
    myApp: { value: '', label: '', type: '' },
    myOffer: { value: '', label: '' },
    title: '',
    customMetadata: '',
    rewards: [],
    categories: [],
    automations: []
  });

  const navigate = useNavigate();

  const {
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(createRequestDealsValidationSchema(formData.dealType)),
    defaultValues: formData
  });

  const handleInputChange = <K extends keyof typeof formData>(
    key: K,
    value: (typeof formData)[K]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value
    }));
    // @ts-ignore
    setValue(key, value);
    // @ts-ignore
    clearErrors(key);
  };

  useEffect(() => {
    getAppsList({
      pageSize: Number(process.env.REACT_APP_PAGE_SIZE_ITEMS),
      currentPage: 1,
      isActive: true
    })
      .then((appsList) => {
        const res = appsList.data?.map((app) => {
          return {
            label: app.name,
            value: app.id,
            type: app.type
          };
        });
        setAppsList(res || []);
      })
      .catch((error: any) => {
        if (error instanceof HttpError) {
          switch (error.statusCode) {
            case statusCode.validation_error:
              setUnexpectedError({
                code: error.statusCode.toString(),
                message: error.message
              });
              break;
            case statusCode.authentication_error:
              localStorage.removeItem(AUTH_TOKEN);
              window.location.href = SIGN_IN_PATH;
              return;
            case statusCode.unexpected_error:
              setUnexpectedError(customErrorMessage);
              break;
            default:
              setUnexpectedError(customErrorMessage);
              break;
          }
        } else {
          setUnexpectedError(customErrorMessage);
        }
        console.error('Failed to get app', error);
      });
  }, []);

  useEffect(() => {
    if (!formData.myApp.value) {
      return;
    }

    handleInputChange('title', generateDealTitle(formData.dealType, props.offer.app.name));
    if (isFeatureEnabled(formData.dealType, 'showRewards')) {
      getRewardsTypes(formData.myApp.value)
        .then((response) => {
          setRewardsTypes(response.data || []);
          handleInputChange('rewards', [{ rewardID: response.data[0].id, value: 100 }]);
        })
        .catch((error: any) => {
          console.error('Failed to fetch rewards types', error);
        });
    }

    if (isFeatureEnabled(formData.dealType, 'requiresMyOffer')) {
      handleInputChange('myOffer', { value: '', label: '' });

      filterController.set(IsActiveAdapter, true);
      filterController.set(DealTypesAdapter, [formData.dealType]);
      getOffers(formData.myApp.value, filterController, {
        pageSize: Number(process.env.REACT_APP_PAGE_SIZE_ITEMS),
        currentPage: 1
      })
        .then((offers) => {
          setOfferData(offers.data || []);
        })
        .catch((error: any) => {
          if (error instanceof HttpError) {
            switch (error.statusCode) {
              case statusCode.validation_error:
                setUnexpectedError({
                  code: error.statusCode.toString(),
                  message: error.message
                });
                break;
              case statusCode.authentication_error:
                localStorage.removeItem(AUTH_TOKEN);
                window.location.href = SIGN_IN_PATH;
                return;
              case statusCode.unexpected_error:
                setUnexpectedError(customErrorMessage);
                break;
              default:
                setUnexpectedError(customErrorMessage);
                break;
            }
          } else {
            setUnexpectedError(customErrorMessage);
          }
          console.error('Failed to load offers:', error);
        });
    }

    if (isFeatureEnabled(formData.dealType, 'showCategories')) {
      getAppCategories(formData.myApp.value)
        .then((response) => {
          setAppCategories(response);
        })
        .catch((error: any) => {
          if (error instanceof HttpError) {
            setUnexpectedError(customErrorMessage);
          }
          console.error('Failed to load app categories:', error);
        });
    }
  }, [formData.myApp]);

  const handleRedirectTsUrl = (url: string) => {
    window.open(url, '_blank');
  };

  const appCategoriesArray = props.offer.app.categories;
  let appCategoriesPart1: any[] = [];
  let appCategoriesPart2: any[] = [];

  if (appCategoriesArray.length > 5) {
    appCategoriesPart1 = [...props.offer.app.categories].slice(0, 5);
    appCategoriesPart2 = [...props.offer.app.categories].slice(
      6,
      props.offer.app.categories.length
    );
  }

  const onSubmit = () => {
    setFormSubmitInProgress(true);
    requestDeal({
      tradeType: formData.dealType,
      myOfferID: formData.myOffer?.value,
      targetOfferID: props.offer.offer.id,
      appID: formData.myApp.value,
      title: formData.title,
      customMetadata: formData.customMetadata ? JSON.parse(formData.customMetadata) : null,
      rewards: formData.rewards,
      categories: formData.categories,
      automations: formData.automations
    })
      .then((response) => {
        setDealID(response.id);
        setOpenSuccessModal(true);
      })
      .catch((error: any) => {
        if (error instanceof HttpError) {
          switch (error.statusCode) {
            case statusCode.validation_error:
              setUnexpectedError({
                code: error.statusCode.toString(),
                message: error.message
              });
              break;
            case statusCode.conflict_error:
              setConflictError(error.info?.error.description || error.message);
              setOpenConflictErrorModal(true);
              break;
            case statusCode.authentication_error:
              localStorage.removeItem(AUTH_TOKEN);
              window.location.href = SIGN_IN_PATH;
              return;
            case statusCode.unexpected_error:
              setUnexpectedError(customErrorMessage);
              break;
            default:
              setUnexpectedError(customErrorMessage);
              break;
          }
        } else {
          setUnexpectedError(customErrorMessage);
        }
        console.error('Failed to fetch deal by id:', error);
      })
      .finally(() => {
        setFormSubmitInProgress(false);
      });
  };

  const sentData = () => {
    handleSubmit(onSubmit)();
  };

  const handleCloseSuccessModal = () => {
    setOpenSuccessModal(false);
    navigate(generatePath(APPS_DEALS_VIEW_PATH, { appID: formData.myApp.value, id: dealID }));
  };

  const handleCloseConflictErrorModal = () => {
    setOpenConflictErrorModal(false);
  };

  const handleCatalogPage = () => {
    setOpenSuccessModal(false);
    navigate(HOME_PATH);
  };

  if (unexpectedError) {
    return (
      <ErrorWindow
        withModal
        title={unexpectedError.code}
        information={unexpectedError.message}
        icon={<SvgIcon src={NotFoundIcon} width={219} height={125} />}
      />
    );
  }

  return (
    <FormContainer>
      <SuccessEntityWindow
        dismissLabel={'View Deal'}
        confirmLabel={'Go to Marketplace'}
        onConfirm={handleCatalogPage}
        onDismiss={handleCloseSuccessModal}
        isOpen={openSuccessModal}
        icon={<SvgIcon src={SuccessIcon} width={195} height={120} />}
        title="Request created"
        information="As soon as your request is accepted, we will notify you of the result."
      />
      <ErrorEntityWindow
        dismissLabel={'Close'}
        onDismiss={handleCloseConflictErrorModal}
        isOpen={openConflictErrorModal}
        icon={<SvgIcon src={NotFoundIcon} width={195} height={120} />}
        title={conflictError}
        information={''}
      />
      <FlexColumn gap={24}>
        <FlexColumn gap={4}>
          <BodyText size={1}>Deal type</BodyText>
          <ItemArea gap={16}>
            <DealTradeInfoSnippet dealType={formData.dealType}></DealTradeInfoSnippet>
          </ItemArea>
        </FlexColumn>
        <FlexColumn gap={4}>
          <BodyText size={1}>Partner app</BodyText>
          <ItemArea gap={16}>
            <FlexColumn gap={4}>
              <StyledCaption>
                <FlexRow gap={4}>
                  <div>{props.offer.app.name}</div>
                  <StyledIconLink
                    onClick={() => handleRedirectTsUrl(props.offer.app.url)}
                    src={LinkIcon}
                    size={16}
                  />
                </FlexRow>
              </StyledCaption>
              <BodyText size={2} scale={'xs'} variation={'darkGray'}>
                {props.offer.app.description}
              </BodyText>
            </FlexColumn>

            <FlexColumn gap={4}>
              <FlexRow gap={8} align={'center'}>
                <BodyText size={3} scale={'sm'} variation={'darkGray'}>
                  MAU:
                </BodyText>
                <BodyText size={3} scale={'sm'} monotype>
                  {props.offer.app.mau.toLocaleString()}
                </BodyText>
                <StyledTooltip tooltipContent="Monthly active users">
                  <SvgIcon src={InfoIcon} color={'gray'} />
                </StyledTooltip>
              </FlexRow>
              <FlexRow gap={8} align={'center'}>
                <BodyText size={3} scale={'sm'} variation={'darkGray'}>
                  DAU:
                </BodyText>
                <BodyText size={3} scale={'sm'} monotype>
                  {props.offer.app.dau.toLocaleString()}
                </BodyText>
                <StyledTooltip tooltipContent="Daily active users">
                  <SvgIcon src={InfoIcon} color={'gray'} />
                </StyledTooltip>
              </FlexRow>
              {appCategoriesArray.length > 5 && appCategoriesPart1.length && (
                <FlexRow gap={8}>
                  {appCategoriesPart1.map((categoryName) => (
                    <CategoryTag key={categoryName} name={categoryName} />
                  ))}
                </FlexRow>
              )}

              {appCategoriesArray.length > 5 && appCategoriesPart2.length && (
                <FlexRow gap={8}>
                  {appCategoriesPart2.map((categoryName) => (
                    <CategoryTag key={categoryName} name={categoryName} />
                  ))}
                </FlexRow>
              )}

              {appCategoriesArray.length <= 5 && (
                <FlexRow gap={8}>
                  {appCategoriesArray.map((categoryName) => (
                    <CategoryTag key={categoryName} name={categoryName} />
                  ))}
                </FlexRow>
              )}
            </FlexColumn>
          </ItemArea>
        </FlexColumn>
        <FlexColumn gap={4}>
          <BodyText size={1}>Partner offer</BodyText>
          <FlexColumn>
            <FlexRow>
              <SourceInfoWrapper justify={'flex-start'} align={'center'} gap={12}>
                <IconWrapper justify={'center'} align={'center'}>
                  <SvgIcon src={TelegramIcon} size={28} />
                </IconWrapper>
                <div>
                  <StyledTitle size={1}>{props.offer.offer.name}</StyledTitle>
                  <StyledDescription>{props.offer.offer.description}</StyledDescription>
                </div>
              </SourceInfoWrapper>
            </FlexRow>
          </FlexColumn>
        </FlexColumn>
        <FlexColumn gap={4}>
          <BodyText size={1}>My App</BodyText>

          {appsList.length ? (
            <Dropdown
              value={formData.myApp}
              items={appsList}
              placeholder={'Select'}
              onSelect={(e) => handleInputChange('myApp', e as unknown as TsDropdownValue)}
            />
          ) : (
            <NoEntityInfoWrapper>
              Seems like you don't have any App. <br /> Please{' '}
              <StyledLink to={APPS_CREATE_PATH}> create App </StyledLink> first
            </NoEntityInfoWrapper>
          )}
          {errors?.myApp?.message && (
            <ErrorTextContainer justify={'flex-start'} top={'46px'}>
              <ErrorText size={3}>{errors?.myApp?.message as string}</ErrorText>
            </ErrorTextContainer>
          )}
        </FlexColumn>
        <div>
          {formData.myApp.value && (
            <FlexColumn gap={24}>
              {/* Configuration-driven form fields */}
              <DealFormFields
                tradeType={formData.dealType}
                formData={formData}
                errors={errors}
                offerData={offerData}
                rewardsTypes={rewardsTypes}
                appCategories={appCategories}
                onFieldChange={(field: any, value: any) => handleInputChange(field as any, value)}
              />

              {/* Automations - shown for all types that support it */}
              {isFeatureEnabled(formData.dealType, 'showAutomations') && (
                <FlexRow>
                  <AutomationsControlsBlock
                    automations={formData.automations}
                    onChange={(updatedAutomations: AutomationRequest[]) => {
                      handleInputChange('automations', updatedAutomations);
                    }}
                    errors={errors?.automations}
                  />
                </FlexRow>
              )}
            </FlexColumn>
          )}
        </div>
      </FlexColumn>

      <StyledButton onClick={sentData} disabled={formSubmitInProgress}>
        Request
      </StyledButton>
    </FormContainer>
  );
};
